// ── Google Play Billing (native Android only) ────────────────────────────────
// Play policy forbids steering users to an alternative payment method for
// in-app digital purchases once the app is distributed via the Play Store, so
// this is the ONLY purchase path allowed inside the native Android build —
// PayMongo/Lemon Squeezy (src/firebase/billing.js's startCheckout) stay for
// web and any non-Play distribution, and billing.js branches between the two
// (see startPlayBillingPurchase there). The web app and iOS are unaffected.
//
// Uses cordova-plugin-purchase (cc.fovea.purchase / "CdvPurchase"), a Cordova
// plugin that talks to Play Billing Library directly — no third-party SaaS
// account needed. Capacitor's Cordova-compatibility layer loads it and
// exposes it as window.CdvPurchase once the native shell boots; it is never
// present on web, so every export here is a no-op off native Android.
//
// A purchase lands in users/{uid}/billing/status via the SAME
// lib/grantPremiumDays used by the PayMongo/Lemon Squeezy webhooks — this
// file never writes Firestore itself, it only gets Google's purchase
// verified server-side (api/payments/verify-play-purchase.js) and then tells
// the plugin the purchase is settled.
import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { toast } from 'sonner';
import { auth } from './config';
import { PLANS, LIFETIME } from './plans';
import { apiUrl } from './apiUrl';

export function isPlayBillingAvailable() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

function getStore() {
  return typeof window !== 'undefined' ? window.CdvPurchase : undefined;
}

// Subscriptions only. Lifetime is a one-time product and is kept separate,
// so "is there a live subscription?" never counts a Lifetime purchase.
const PLAY_PRODUCT_IDS = Object.values(PLANS).map((p) => p.playProductId);

// planId -> the catalog entry purchasePlayBillingPlan / the price hook use.
function planFor(planId) {
  return planId === LIFETIME.id ? LIFETIME : PLANS[planId];
}

// ── Purchase-confirmation pending flag ────────────────────────────────────────
// True from the moment Play reports a purchase approved until our server has
// finished verifying + granting it (or failed and told the user). Exists so
// PremiumRow can show "Confirming your purchase…" during that window instead
// of the plain Upgrade button, which otherwise reads as nothing happening for
// several real seconds — the round trip through the Play Developer API (see
// lib/googlePlayBilling.js's retry backoff, there on purpose: Google's own
// API can lag a few seconds behind the purchase actually completing) plus our
// own verify + grant. Shortening THAT wait risks the false "couldn't confirm
// your purchase" error #203 was written to prevent; this only makes the wait
// legible instead of making it shorter.
//
// A plain module-level flag + listener set, not Context, since this needs to
// be readable from a purely client-side plugin callback with no component
// tree of its own to thread state through.
let purchasePending = false;
const purchasePendingListeners = new Set();
function setPurchasePending(value) {
  if (purchasePending === value) return;
  purchasePending = value;
  purchasePendingListeners.forEach((fn) => fn(value));
}

// True between a purchase being approved and its verification settling
// (success or failure). Always false off native Android, since nothing ever
// calls setPurchasePending there.
export function usePlayPurchasePending() {
  const [pending, setPending] = useState(purchasePending);
  useEffect(() => {
    purchasePendingListeners.add(setPending);
    return () => purchasePendingListeners.delete(setPending);
  }, []);
  return pending;
}

let initPromise = null;

// Registers the 3 subscription products plus the one-time Lifetime product, wires the approve → server-verify →
// finish pipeline, and starts the plugin talking to Play Billing. Safe to
// call more than once — subsequent calls reuse the first initialization.
export function initPlayBilling() {
  if (!isPlayBillingAvailable()) return Promise.resolve(false);
  if (initPromise) return initPromise;

  initPromise = new Promise((resolve) => {
    const cdv = getStore();
    if (!cdv) {
      // Plugin didn't load — treat as unavailable rather than throwing, so a
      // build without the native plugin bundled still runs (just without
      // premium purchases). See docs/play-billing-setup.md for the required
      // `npx cap sync android` step that pulls the plugin in.
      resolve(false);
      return;
    }
    const { store, ProductType, Platform } = cdv;

    store.register([
      ...PLAY_PRODUCT_IDS.map((id) => ({
        id,
        type: ProductType.PAID_SUBSCRIPTION,
        platform: Platform.GOOGLE_PLAY,
      })),
      // Lifetime: bought once, owned forever. NON_CONSUMABLE makes the
      // plugin's finish() acknowledge it rather than consume it (consuming
      // would let it be bought again and drop it from restorePurchases).
      { id: LIFETIME.playProductId, type: ProductType.NON_CONSUMABLE, platform: Platform.GOOGLE_PLAY },
    ]);

    // Tags every purchase with the signed-in user's Firebase uid as Play
    // Billing's "obfuscated account id". Google echoes this back on
    // purchases.subscriptionsv2.get as externalAccountIdentifiers.
    // obfuscatedExternalAccountId (see lib/googlePlayBilling.js), which is
    // how api/payments/play-rtdn.js — a server-to-server renewal
    // notification with no ID token to verify — knows whose entitlement to
    // extend. Without this, renewals would have no way to find the user.
    store.applicationUsername = () => auth.currentUser?.uid || '';

    // The plugin calls this with every purchase that needs verifying. Only
    // report ok:true once OUR server has confirmed the grant landed — never
    // optimistically — so the plugin only finish()es (which triggers Play's
    // own acknowledge) a purchase we know actually paid off.
    //
    // SHAPE OF `body` — cordova-plugin-purchase v13 does NOT hand a Receipt or
    // a Transaction to a function-style validator. It hands the receipt-
    // validation REQUEST BODY it would otherwise POST to a validation service.
    // See Validator.Function in node_modules/cordova-plugin-purchase/www/
    // store.d.ts, and the GooglePlay adapter's receiptValidationBody() in
    // store.js that builds it:
    //
    //   { id: '<productId>', type, offers, products,
    //     transaction: { type: 'android-playstore', id: '<orderId>',
    //                    purchaseToken, signature, receipt },
    //     additionalData: { applicationUsername, obfuscatedUsername } }
    //
    // The purchase token is at body.transaction.purchaseToken (singular) and
    // the product id at body.id. There is no body.transactions[] array and no
    // body.nativePurchase — reading those, as this code used to, left
    // purchaseToken undefined for EVERY purchase, so verifyPurchaseOnServer()
    // threw 'Malformed purchase receipt' before its fetch ever went out. That
    // is exactly why a real, charged purchase produced a failure toast while
    // Vercel logged zero requests to /api/payments/verify-play-purchase.
    //
    // SHAPE OF `callback` — also a plain function taking a validation response
    // payload, `(payload) => void`, not an object carrying .approved()/
    // .failed() methods. callback.approved() threw a TypeError that the plugin
    // caught and swallowed into a log line, so even a verification that HAD
    // reached the server could never have settled the transaction.
    store.validator = async (body, callback) => {
      try {
        const tx = body?.transaction || {};
        // See usePlayPurchasePending() above — cleared in the finally below
        // regardless of which path out of this function runs.
        setPurchasePending(true);
        const purchaseToken = tx.purchaseToken;
        const productId = body?.id;
        const orderId = tx.id;
        await verifyPurchaseOnServer({ purchaseToken, productId, orderId });
        // Minimal success payload (CdvPurchase.Validator.Response.SuccessPayload).
        // `collection` is deliberately omitted: the plugin only consults it to
        // decide whether it may SKIP the on-device acknowledge, so leaving it
        // empty makes the plugin acknowledge too. verify-play-purchase.js
        // already acknowledged server-side and Google no-ops a repeat
        // acknowledge, making the double call harmless — and strictly safer
        // than claiming it's already handled.
        callback({
          ok: true,
          data: {
            id: productId,
            latest_receipt: true,
            transaction: { type: Platform.GOOGLE_PLAY },
          },
        });
      } catch (e) {
        console.error('[playBilling] server verification failed:', e.message);
        // Restoring replays whatever purchase lives on the DEVICE's Google
        // account, which is not necessarily this Clarity account's — someone
        // signing a second account into their own phone hits this every boot.
        // The server refusing to grant is the CORRECT outcome there, not a
        // failure to report: toasting it would tell a user with no purchase
        // that their purchase went wrong. Stays silent, logs, and settles the
        // transaction as unverified.
        if (e.code === 'purchase_belongs_to_another_account') {
          callback({
            ok: false,
            code: cdv.ErrorCode?.VERIFICATION_FAILED,
            message: 'Purchase belongs to another account',
          });
          return;
        }
        // The purchase flow's own promise (purchasePlayBillingPlan) has
        // already resolved by this point — this fires later, once Google's
        // async approved/verify pipeline actually runs — so without this the
        // Upgrade button shows no error at all: a real charge would look
        // fine to the payer with nothing telling them it didn't unlock
        // anything. The transaction stays unfinished on a failure like this,
        // so it retries automatically next app open (see the initPlayBilling
        // call in App.jsx) — this is just making the in-the-moment failure
        // visible instead of only ever landing in a device console log.
        // The reason itself stays in the console rather than the toast: it's
        // server/plugin vocabulary ('purchase_not_active', 'invalid_token'),
        // meaningless to a payer and alarming out of context. The versionCode
        // 8 round that carried it in the toast has served its purpose — it
        // confirmed the body/callback fix reaches the server (200 on
        // /api/payments/verify-play-purchase) and unlocks premium.
        toast.error("We couldn't confirm your purchase yet. It'll retry automatically. If it still hasn't unlocked in a few minutes, reopen the app.");
        callback({
          ok: false,
          code: cdv.ErrorCode?.VERIFICATION_FAILED,
          message: e.message,
        });
      } finally {
        // Runs on every exit path above (success, ownership mismatch, and
        // the generic failure), so "Confirming your purchase…" never gets
        // stuck showing after the server has actually answered.
        setPurchasePending(false);
      }
    };

    store.when().approved((transaction) => transaction.verify());
    store.when().verified((receipt) => receipt.finish());
    store.error((err) => console.error('[playBilling] store error:', err.message || err));

    store.initialize([Platform.GOOGLE_PLAY]).then(() => resolve(true));
  });

  return initPromise;
}

// ── Live localized prices ─────────────────────────────────────────────────────
// Google Play is the only source of truth for what a plan actually costs in
// the buyer's country/currency — it localizes automatically per Play
// Console's price list, which this app never mirrors. PLANS' php/usd fields
// (plans.js) exist only for the PayMongo/Lemon Squeezy web checkout; showing
// them on Android would risk not matching what Play's own purchase sheet
// charges a second later. This hook surfaces the SAME price Play already
// fetched into the plugin's product cache, keyed by plan id, so a purchase
// page can show real per-country pricing before the sheet even opens.
let pricesCache = {};
const priceListeners = new Set();
function setPrices(next) {
  pricesCache = next;
  priceListeners.forEach((fn) => fn(pricesCache));
}

// Play's raw amount per plan ({ value, currency }), read alongside the price
// strings below. Only used for the purchase page's "Save N%" / "per month"
// lines, which have to do arithmetic; the price itself is always Play's string.
let amountsCache = {};
export function playPriceAmounts() { return amountsCache; }

function readPricesFromStore() {
  const cdv = getStore();
  if (!cdv) return {};
  const { store } = cdv;
  const next = {};
  const amounts = {};
  for (const [planId, plan] of [...Object.entries(PLANS), [LIFETIME.id, LIFETIME]]) {
    const offer = store.get(plan.playProductId)?.getOffer();
    // pricingPhases[0].price is already Play's own localized, formatted
    // string (e.g. "$4.99", "₱149.00") — never re-derived from priceMicros,
    // since formatting currency correctly per-locale is exactly what we're
    // relying on Play to do for us.
    const phase = offer?.pricingPhases?.[0];
    const price = phase?.price;
    if (price) next[planId] = price;
    if (phase?.priceMicros > 0 && phase?.currency) amounts[planId] = { value: phase.priceMicros / 1e6, currency: phase.currency };
  }
  amountsCache = amounts;
  return next;
}

// Returns { [planId]: "₱149.00" } (planId includes 'lifetime') for whichever plans Play has already
// returned pricing for. Empty (or missing a plan) until initPlayBilling()'s
// product fetch resolves — callers should treat an absent plan as "still
// loading" rather than "free"/"unavailable". Always empty off native Android.
export function usePlayBillingPrices() {
  const [prices, setLocalPrices] = useState(pricesCache);
  useEffect(() => {
    if (!isPlayBillingAvailable()) return;
    priceListeners.add(setLocalPrices);
    initPlayBilling().then((ready) => {
      if (!ready) return;
      setPrices(readPricesFromStore());
      // Fires whenever the plugin (re)loads product/offer data from Play —
      // covers the case where this hook mounts before that fetch finishes.
      getStore()?.store?.when()?.productUpdated(() => setPrices(readPricesFromStore()));
    });
    return () => priceListeners.delete(setLocalPrices);
  }, []);
  return prices;
}

// Sends the purchase to our server for verification against the Play
// Developer API and, on success, a grantPremiumDays call — mirrors the
// Authorization: Bearer <idToken> pattern api/payments/create-checkout.js
// already uses for PayMongo. Uses apiUrl() since this always runs on native
// Android, where a plain relative fetch would silently hit the app's own
// bundled WebView instead of the real server (see apiUrl.js) — that would
// make a real Play purchase look "verified" client-side while never actually
// reaching Firestore, so the buyer would pay without ever getting Premium.
async function verifyPurchaseOnServer({ purchaseToken, productId, orderId }) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  if (!purchaseToken || !productId) throw new Error('Malformed purchase receipt');
  const idToken = await user.getIdToken();
  const res = await fetch(apiUrl('/api/payments/verify-play-purchase'), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ purchaseToken, productId, orderId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || data.error || 'Purchase could not be verified');
    // Carried through so the validator can tell an expected refusal apart from
    // a real failure — see the ownership case there.
    err.code = data.error;
    throw err;
  }
  return data;
}

// Launches the Play Billing purchase sheet for the given plan
// ('weekly' | 'monthly' | 'yearly' | 'lifetime'). Resolves once the purchase flow has
// STARTED, not once it's complete — completion (or cancellation) arrives
// asynchronously via the store.when() handlers registered in
// initPlayBilling(), same as any Play Billing integration. Throws if the
// plugin isn't available or the product hasn't loaded from Play yet.
export async function purchasePlayBillingPlan(planId) {
  const plan = planFor(planId);
  if (!plan) throw new Error('Unknown plan');
  const ready = await initPlayBilling();
  if (!ready) throw new Error('Play Billing is not available on this device');

  const cdv = getStore();
  const { store } = cdv;
  const product = store.get(plan.playProductId);
  const offer = product?.getOffer();
  if (!offer) {
    // Not yet loaded from Play (or the product doesn't exist in Play Console
    // yet — see docs/play-billing-setup.md), or the user already owns it.
    // There's no pull-to-refresh UI anywhere in the app — the real recovery
    // path is the restorePlayBillingPurchases() call in App.jsx's boot
    // effect, which runs automatically next time this screen (or the app)
    // reopens. Worded as "reopen" rather than "refresh" since that's the
    // actual, real affordance available.
    if (product?.owned) {
      throw new Error(planId === LIFETIME.id
        ? "You already own Lifetime Premium. If it hasn't unlocked yet, close and reopen the app."
        : "You already have an active subscription. If it hasn't unlocked yet, close and reopen the app.");
    }
    throw new Error('Could not load this plan from Google Play. Please try again in a moment.');
  }

  const result = await store.order(offer);
  // cordova-plugin-purchase resolves order() with an IError object on
  // failure/cancellation instead of rejecting, per its documented API.
  if (result && result.isError) {
    if (result.code === cdv.ErrorCode?.PAYMENT_CANCELLED) {
      throw new Error('cancelled');
    }
    throw new Error(playErrorMessage(result, cdv));
  }
}

// Turns a cordova-plugin-purchase IError into something a payer can act on.
// Its `message` is Play's own vocabulary — a debug build tapping Upgrade
// surfaced the bare string 'ITEM_UNAVAILABLE' straight into a toast, which
// tells a user nothing and reads like a crash. Codes we can explain get real
// sentences; anything else falls back to a generic line, with the raw message
// kept in the console for us rather than shown to them.
function playErrorMessage(err, cdv) {
  const E = cdv.ErrorCode || {};
  console.error('[playBilling] order failed:', err.code, err.message);
  switch (err.code) {
    case E.PRODUCT_NOT_AVAILABLE:
    case E.INVALID_PRODUCT_ID:
      return 'This plan is not available on your account right now. Please try again later.';
    case E.PAYMENT_NOT_ALLOWED:
    case E.CLIENT_INVALID:
      return "Your Google account isn't allowed to make purchases. Check your Play Store payment settings and try again.";
    case E.PAYMENT_INVALID:
      return 'Google Play could not process that payment. Please check your payment method and try again.';
    case E.COMMUNICATION:
    case E.BAD_RESPONSE:
      return "Couldn't reach Google Play. Check your connection and try again.";
    case E.SUBSCRIPTIONS_NOT_AVAILABLE:
      return 'Subscriptions are not available on this device.';
    default:
      return 'Purchase could not be started. Please try again in a moment.';
  }
}

// Re-checks entitlements the plugin already knows about (e.g. after
// reinstalling, or on a fresh sign-in) without opening the purchase sheet
// again. Each restored transaction still runs through the same
// approved → verify → server-verify → finish pipeline as a fresh purchase.
export async function restorePlayBillingPurchases() {
  const ready = await initPlayBilling();
  if (!ready) return;
  const { store } = getStore();
  await store.restorePurchases();
}

// True when Google Play reports a live subscription on THIS device's Google
// account. Used by the delete-account flow, which needs to warn that deleting
// a Clarity account does not cancel the Play subscription behind it — only the
// user can do that, from the Play Store. Nothing in the app can cancel it on
// their behalf, so the honest move is to say so before the account is gone.
//
// Deliberately answers false rather than throwing when Play Billing isn't
// available or hasn't loaded: a missing warning is a worse outcome than a
// wrong one only if it's wrong in the other direction, and there is nothing
// useful to tell a user the store can't confirm is subscribed.
export async function hasActivePlaySubscription() {
  try {
    const ready = await initPlayBilling();
    if (!ready) return false;
    const { store } = getStore();
    return PLAY_PRODUCT_IDS.some((id) => store.get(id)?.owned === true);
  } catch (e) {
    console.error('[playBilling] subscription check failed:', e.message);
    return false;
  }
}

// True when THIS device's Google account has a Play subscription that is still
// set to renew. Used to remind a Lifetime owner that buying Lifetime did not
// cancel the subscription they had before — Google keeps charging it until
// they cancel it in the Play Store, and the app can't do that for them. A
// subscription already cancelled (set to lapse at the end of its period) is
// not counted: there's nothing left for them to do about it.
//
// Same caveat as hasActivePlaySubscription(): false until the plugin has
// loaded receipts, and always false off native Android.
export async function hasRenewingPlaySubscription() {
  try {
    const ready = await initPlayBilling();
    if (!ready) return false;
    const { store, RenewalIntent } = getStore();
    return PLAY_PRODUCT_IDS.some((id) => {
      const product = store.get(id);
      if (!product?.owned) return false;
      const tx = store.findInLocalReceipts(product);
      return tx?.renewalIntent !== (RenewalIntent?.LAPSE || 'Lapse');
    });
  } catch (e) {
    console.error('[playBilling] renewing-subscription check failed:', e.message);
    return false;
  }
}

// Hook form of the above, re-checked whenever Play reloads receipts (so it
// turns true once they've loaded, and false once the user has cancelled in
// the Play Store and come back). `enabled` lets callers skip the check
// entirely when it can't matter (e.g. the account doesn't hold Lifetime).
export function useRenewingPlaySubscription(enabled) {
  const [renewing, setRenewing] = useState(false);
  useEffect(() => {
    if (!enabled || !isPlayBillingAvailable()) {
      setRenewing(false);
      return undefined;
    }
    let cancelled = false;
    const check = () => hasRenewingPlaySubscription().then((v) => { if (!cancelled) setRenewing(v); });
    check();
    const store = getStore()?.store;
    store?.when()?.receiptUpdated?.(check);
    return () => {
      cancelled = true;
      store?.off?.(check);
    };
  }, [enabled]);
  return renewing;
}
